import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { Form } from '@hrlogics/react-components/form';

import type { Nullable } from '@clearverify/types';

import {
  ActiveInactive,
  ConsumerDocument,
  ConsumerQuery,
  useConsumerUpdateMutation,
  useSendRequestAccountIsFrozenMutation,
} from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { theme } from 'src/theme';
import type { SxProp } from 'src/types/mui';

import { ColorSchema } from './types';

interface ActivityLogStateAccountProps {
  consumer: Nullable<ConsumerQuery['employee']>;
  consumerFreeze?: Nullable<ActiveInactive>;
}

export const ActivityLogStateAccount: React.FC<ActivityLogStateAccountProps> = ({
  consumerFreeze,
  consumer,
}) => {
  const { isMobile } = useScreenInfo();
  const { createApolloContext } = useNotification();

  const [consumerUpdate] = useConsumerUpdateMutation({
    refetchQueries: [ConsumerDocument],
    context: createApolloContext({
      successMessage: 'The changes have been saved',
    }),
  });

  const [sendNotificationByAccountFrozen] = useSendRequestAccountIsFrozenMutation();

  const onSubmit = React.useCallback(async () => {
    if (!consumer?.id) return;

    try {
      if (consumerFreeze === ActiveInactive.Inactive) {
        await consumerUpdate({
          variables: {
            where: { id: consumer.id },
            data: {
              freeze: ActiveInactive.Active,
            },
          },
        });

        await sendNotificationByAccountFrozen({
          variables: {
            employeeId: consumer.id,
          },
        });
      } else {
        await consumerUpdate({
          variables: {
            where: { id: consumer.id },
            data: {
              freeze: ActiveInactive.Inactive,
            },
          },
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error(error);
      }
    }
  }, [consumer?.id, consumerFreeze, consumerUpdate, sendNotificationByAccountFrozen]);

  const colorSchema = React.useMemo((): ColorSchema => {
    switch (consumerFreeze) {
      case ActiveInactive.Active:
        return {
          text: theme.palette.secondary.contrastText,
          container: theme.palette.action.active,
        };
      case ActiveInactive.Inactive:
        return { text: theme.palette.primary.dark, container: theme.palette.primary.dark };
      default:
        return { text: theme.palette.primary.dark, container: theme.palette.primary.dark };
    }
  }, [consumerFreeze]);

  return (
    <Grid
      container
      rowGap={1}
      direction="row"
      sx={containerStyles}
      color={colorSchema.container}
      justifyContent="space-between"
      alignItems="center"
    >
      <Grid direction="column" size={isMobile ? 9 : undefined}>
        <Grid container direction="row" wrap="nowrap" gap={1.5} color={colorSchema.container}>
          <Grid container gap={0.5} direction="column" wrap="nowrap">
            <Box color={colorSchema.text}>
              <Typography color={colorSchema.text}>
                {ActiveInactive.Active === consumerFreeze ? 'Frozen' : 'Active'}
              </Typography>
            </Box>
            {ActiveInactive.Active === consumerFreeze ? (
              <Typography variant="body2" color={colorSchema.text}>
                All verifications except government verifiers will be declined
              </Typography>
            ) : (
              <Typography variant="body2" color={colorSchema.text}>
                Your account is not frozen; therefore, commercial verifications will be completed as
                usual.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid size={isMobile ? 3 : undefined}>
        <Form onSubmit={onSubmit}>
          {({ submitting }) => (
            <Button
              type="submit"
              color={ActiveInactive.Active === consumerFreeze ? 'secondary' : 'primary'}
              onClick={() => onSubmit}
              loading={submitting}
              disabled={submitting}
              sx={{ p: isMobile ? 0 : 'auto' }}
            >
              {ActiveInactive.Active === consumerFreeze ? 'Activate account' : 'Freeze account'}
            </Button>
          )}
        </Form>
      </Grid>
    </Grid>
  );
};

const containerStyles: SxProp = theme => ({
  padding: 2,
  border: `1px solid`,
  borderRadius: 2,
  backgroundColor: theme.palette.common.white,
});
